<template>
    <div class="login-container">
        <Head>
            <title>Next Gen Math - Log in</title>
        </Head>

        <orange-background></orange-background>
        <div class="login-form-container h4 alert alert-danger" v-if="!browserIsSupported">
            <div class="mb-3" v-if="safariHasExpired">We are sorry to inform you that your browser (Safari 12.1) is no longer supported. Please contact your administrator to upgrade or switch to an alternative browser such as <a href="https://www.google.com/chrome/">Google Chrome</a>. </div>
            <span v-else>You will not be able to use your current browser with Next Gen Math after <strong>03/10/2023</strong> due to security concerns. </span>
            <a :href="`${routes.support_url}/articles/8437011096859-Supported-browsers`">See full list of supported browsers here</a>.
        </div>
        <b-form 
            v-if="browserIsSupported || !safariHasExpired" 
            name="login-form"
            class="login-form-container bg-white b-r-md m-t-md p-t-sm p-l-md p-r-md"
            @submit.prevent="submit" >
            <h2>Sign in</h2>
            <b-row>
                <b-col xs="12" sm="6" md="6" class="mt-3">
                    <b-form-group label="" v-slot="{ ariaDescribedby }">
                        <b-form-radio-group
                            id="btn-radios-1"
                            v-model="activeTabName"
                            :options="['School', 'District']"
                            :aria-describedby="ariaDescribedby"
                            name="radios-btn-default"
                            buttons
                            button-variant="primary">
                        </b-form-radio-group>
                    </b-form-group>
                    <b-row class="fields-container">
                        <b-form-group label="Username">
                            <field-error v-if="$v.username.$error && $v.username.$dirty" :validator="$v.username" />
                            <b-form-input 
                                name="username"
                                v-model.trim="$v.username.$model"
                                :state="!$v.username.$error"
                                type="text"
                                placeholder="Username or email" 
                                autocorrect="off" 
                                autocapitalize="none" />
                        </b-form-group>
                        <b-form-group label="Password">
                            <field-error v-if="$v.password.$error && $v.password.$dirty" :validator="$v.password" />
                            <b-form-input 
                                name="password"
                                v-model.trim="$v.password.$model"
                                :state="!$v.password.$error"
                                type="password"
                                placeholder="Password" 
                                autocorrect="off" 
                                autocapitalize="none" />
                        </b-form-group>
                        <b-form-group :label="activeTabName">
                            <field-error v-if="$v.selectedSite.$dirty && $v.selectedSite.$error" :validator="$v.selectedSite" />
                            <site-lookup 
                                :type="activeTabName.toLowerCase()" 
                                v-model="$v.selectedSite.$model" 
                                :options="{
                                    multiple: false,
                                    searchable: true,
                                    limit: 25,
                                    closeOnSelect: true,
                                    clearOnSelect: true,
                                    showLabels: false,
                                }"
                                @input="$refs.loginBtn.focus()" />
                        </b-form-group>
                    </b-row>
                    <b-button 
                        type="submit" 
                        id="sign-in"
                        class="btn btn-primary full-width m-b-sm"
                        :disabled="submitDisabled"
                        ref="loginBtn"
                        variant="primary"
                        block >
                        Sign in
                    </b-button>  
                    <b-form-group class="my-2">
                        <Link href="/login/password-reset-request">Forgot your password?</Link> 
                    </b-form-group>
                    
                    <div v-if="errorMessage[activeTabName]" class="mt-sm">
                        <span class="text-danger" v-html="errorMessage[activeTabName]"></span>
                    </div>
                    <div v-if="errorMessage['Single sign-on']" class="mt-sm">
                        <span class="text-danger" v-html="errorMessage['Single sign-on']"></span>
                    </div>
                </b-col>
                <b-col xs="12" sm="6" md="6" class="mt-3">
                    <button 
                        type="button" 
                        class="login-badge-btn"
                        @click="$refs['modal'].show()">
                        <img :src="`${routes.cdn_url}/images/login/ngm-login-badge-ico.png`" alt="Next Gen Math badge sign in" />
                        <div class="btn-bottom">Badge sign in</div>
                    </button>
                    <sso-links></sso-links>
                </b-col>
            </b-row>
            <hr>
            <b-row>
                <b-col xs="12" sm="6" md="6" class="mt-3">
                    <b-form-group>
                        <div>Don't have an account?</div>
                        <Link href="/free-trial">Request a free pilot!</Link>
                    </b-form-group>
                </b-col>
                <b-col xs="12" sm="6" md="6" class="mt-3 d-flex justify-content-end">
                    <a :href="routes.support_url + '/sections/8435724447643-Sign-In'" target="_blank">Need help?</a>
                </b-col>
            </b-row>
            <!-- Prevent HubSpot from capturing form -->
            <label for="cc-num" class="d-none">credit card HubspotCollectedFormsWorkaround https://community.hubspot.com/t5/APIs-Integrations/How-to-stop-collected-forms-from-collecting-a-form/m-p/299172#M28102</label>
            <input name="cc-num" class="d-none" required="" value="HubspotCollectedFormsWorkaround" id="cc-num">
        </b-form>
        <b-modal id="login-badge-modal" ref="modal" hide-footer centered title="Scan">
            <badge-reader></badge-reader>
        </b-modal>
    </div>
</template>

<script>
    import SiteLookup from '@root/js/components/SiteLookup.vue';
    import FieldError from '@root/js/components/inputs/FieldError.vue';
    import SsoLinks from './SsoLinks.vue';
    import BadgeReader from './BadgeReader.vue'
    import OrangeBackground from '@nextgenmath/js/components/OrangeBackground.vue';
    import { required } from '@root/js/validators';
    import { BFormRadio, BFormRadioGroup, BFormInput, BModal } from 'bootstrap-vue';
    import { detect } from 'detect-browser';
    const browser = detect();

    import Layout from '@nextgenmath/js/pages/Layout.vue';

    export default {
        layout: Layout,
        data: () => ({
            username: null,
            password: null,
            selectedSite: null,
            activeTabName: 'School',
            submitDisabled: false,
            errorMessage: {
                School: null,
                District: null,
                'Single sign-on': null,
            },
            showBadgeReader: false,
        }),
        validations: {
            username: {
                required,
            },
            password: {
                required,
            },
            selectedSite: {
                required
            },
        },
        computed: {
            siteId() {
                return this.selectedSite.id || null;
            },
            referrer() {
                const params = new URLSearchParams(window.location.search);

                return params.get('ref') || null;
            },
            browserIsSupported() {
                console.log("browser", browser);

                return browser.name != "safari" || (browser.name == "safari" && browser.version >= "13.0");
            },
            safariHasExpired() {
                return Date.now() >= new Date('03/11/2023');
            }
        },
        methods: {
            async submit() {
                this.$v.$touch();

                if (!this.$v.$invalid && !this.submitDisabled) {
                    this.errorMessage[this.activeTabName] = null;
                    this.submitDisabled = true;
                    
                    Swal.fire({
                        title: 'Signing in...',
                        allowOutsideClick: false,
                        showConfirmButton: false,
                        didOpen: () => {
                            Swal.showLoading()
                        }
                    });

                    return await axios.post(`${this.routes.api_url}/v1.0/login`, {
                        username: this.username,
                        password: this.password,
                        site_id: this.siteId,
                        site_type: this.activeTabName.toLowerCase(),
                    }).then(response => {
                        this.submitDisabled = false;

                        if (response.data.status && response.data.status == 'failed') {
                            Swal.close();
                            return this.errorMessage[this.activeTabName] = response.data.message;
                        }
                        
                        this.rememberSite();
                        let redirectRegex = new RegExp('^(' + this.routes.ngm_url + '|' + this.routes.api_url + ')');

                        if (this.referrer && redirectRegex.test(this.referrer))
                            return window.location.href = this.referrer;

                        if (response.data.redirect_uri && redirectRegex.test(response.data.redirect_uri))
                            return window.location.href = response.data.redirect_uri;

                        return window.location.href = '/assignments';
                    }, error => {
                        Swal.close();
                        this.submitDisabled = false;

                        this.errorMessage[this.activeTabName] = error.response.status;
                    });
                }
            },
            rememberSite() {
                let data = {
                    type: this.activeTabName,
                    site: this.selectedSite
                };          

                localStorage.setItem('remember_last_successful_site', JSON.stringify(data))
            },
            setDefaults() {
                let data = JSON.parse(localStorage.getItem('remember_last_successful_site'));

                this.activeTabName = data.type;
                this.selectedSite = data.site;
            },
        },
        created() {
            // redirect user to dashboard if authenticated
            if (this.user)
                window.location.href = this.routes.ngm_url + '/assignments';
        },
        mounted() {
            this.$nextTick(() => {
                if (localStorage.getItem('remember_last_successful_site'))
                    this.setDefaults();

                if (this.status && this.status.code !== 200) {
                    if (this.status.type == 'SSO') {
                        this.errorMessage['Single sign-on'] = this.status.message;
                        this.activeTabName = 'School';
                    } else {
                        this.errorMessage[this.activeTabName] = this.status.message;
                    }
                }
            });
        },
        watch: {
            activeTabName: function() {
                this.selectedSite = null;
            },
        },
        props: {
            state: {
                type: String,
                default: ''
            },
            status: {
                type: Object,
            },
        },
        components: {
            OrangeBackground,
            SiteLookup,
            SsoLinks,
            BadgeReader,
            FieldError,
            BFormRadio,
            BFormRadioGroup,
            BFormInput,
            BModal,
        }
    }
</script>

<style lang="scss" scoped>
    .login-form-container {
        display: block;
        max-width: 756px;
        min-width: 330px;
        margin: 15vh auto;
        padding: 1rem 1.5rem;
        border-radius: 6px;
        box-shadow: 4px 4px 24px rgba(0, 0, 0, 0.15);

        #btn-radios-1 {
            width: 100%;
        }

        .fields-container {
            padding-left: 15px;
            padding-right: 15px;

            fieldset {
                width: 100%;
            }
        }
    }

    h4.or-split {
        width: 100%;
        display: table;
        white-space: nowrap;
    }

    h4.or-split:before, 
    h4.or-split:after {
        border-top: 1px solid #337ab7;
        content: '';
        display: table-cell;
        position: relative;
        top: 0.5em;
        width: 50%;
    }

    h4.or-split:before {
        right: 1.5%;
    }

    h4.or-split:after {
        left: 1.5%;
    }
    
    .sso-container {
        /* padding-top: 50px; */
        vertical-align: bottom;
    }

    .login-badge-btn {
        width: 100%;
        padding: 0 !important;
        border: 1px solid #ebebeb;
        border-radius: 5px;
        overflow: hidden;
        transition: box-shadow 250ms;
        text-align: center;
        background: #fff;
        margin-bottom: 1.5rem;

        &:hover {
            box-shadow: 0 3px 10px rgb(0 0 0 / 0.15);

            .btn-bottom {
                background-color: #337AB7;
            }
        }

        img { 
            width: 264px;
        }

        .btn-bottom {
            width: 100%;
            line-height: 48px;
            color: #fff;
            background-color: #3987D8;
            text-align: center;
            transition: background-color 250ms;
            font-size: 1.2rem;
        }
    }

    @media (max-width: 992px) {
        .login-form-container {
            margin: 5vh auto;
        }
    }
    @media (max-width: 768px) {
        .login-form-container {
            margin: 2vh auto;
        }
    }
    @media (max-width: 576px) {
        .login-form-container {
            width: 90%;
        }
    }
    // Bootstrap 4 modal badge style
    #login-badge-modal {
        .modal-content {
            border: 0 !important;
        }

        .modal-container, .modal-body {
            padding: 0 !important;
        }

        .modal-header {
            background-color: #296DCF;
            color: #fff;
            padding: 10px;
            border-bottom: 0!important;
            
            .close {
                color: #fff;
            }
        }        
    }
</style>
